import Modal from "react-bootstrap/Modal";
import classes from "./PostDetailModal.module.css";

function formatDate(value) {
  if (!value) return "—";
  try {
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleString();
    }
    if (value?.seconds) {
      return new Date(value.seconds * 1000).toLocaleString();
    }
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

function getYoutubeEmbedUrl(url) {
  if (!url) return null;
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([^&?/]+)/
  );
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

function PostDetailModal({ show, post, onHide }) {
  if (!post) return null;

  const youtubeEmbed = getYoutubeEmbedUrl(post.url);
  const attachments = Array.isArray(post.attachments) ? post.attachments : [];
  const reports = Array.isArray(post.reports) ? post.reports : [];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {post.title || "Post Details"}
          {reports.length > 0 && (
            <span className="badge bg-danger ms-2">{reports.length} reports</span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={classes.mediaWrap}>
          {youtubeEmbed ? (
            <iframe
              title={post.title || "Video"}
              src={youtubeEmbed}
              className={classes.videoFrame}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : post.url ? (
            <video controls className={classes.videoPlayer} src={post.url} poster={post.thumbnail || undefined}>
              Your browser does not support the video tag.
            </video>
          ) : (
            <div className={classes.noMedia}>No video URL</div>
          )}
        </div>

        <div className={classes.metaGrid}>
          <DetailRow label="Title" value={post.title || "—"} />
          <DetailRow label="Description" value={post.description || "—"} multiline />
          <DetailRow label="Category" value={post.category || "—"} />
          <DetailRow label="Subcategory" value={post.subcategory || "—"} />
          <DetailRow label="Uploader" value={post.uploader_details?.name || "—"} />
          <DetailRow label="Email" value={post.uploader_details?.email || "—"} />
          <DetailRow label="Views" value={String(post.views ?? 0)} />
          <DetailRow label="Uploaded" value={formatDate(post.date_uploaded)} />
          <DetailRow
            label="Status"
            value={
              post.is_pending
                ? "Pending"
                : post.is_slider
                  ? `Slider #${post.slider_order ?? 0}`
                  : "Active"
            }
          />
          {post.upload_type && (
            <DetailRow label="Upload type" value={post.upload_type} />
          )}
          {post.social_media_link?.trim() && (
            <DetailRow
              label="Additional Link"
              value={
                <a href={post.social_media_link} target="_blank" rel="noreferrer">
                  {post.social_media_link}
                </a>
              }
            />
          )}
          {post.url && (
            <DetailRow
              label="Video URL"
              value={
                <a href={post.url} target="_blank" rel="noreferrer">
                  Open in new tab
                </a>
              }
            />
          )}
        </div>

        {attachments.length > 0 && (
          <div className={classes.attachmentsSection}>
            <h6>Attached Images ({attachments.length})</h6>
            <div className={classes.attachmentsGrid}>
              {attachments.map((item, index) => (
                <a
                  key={`${item.url}-${index}`}
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className={classes.attachmentItem}
                  title={item.name || `Image ${index + 1}`}
                >
                  <img src={item.url} alt={item.name || `Attachment ${index + 1}`} />
                  {item.name && <span>{item.name}</span>}
                </a>
              ))}
            </div>
          </div>
        )}

        {reports.length > 0 && (
          <div className={classes.reportsSection}>
            <h6>Reports ({reports.length})</h6>
            <div className={classes.reportsList}>
              {reports.map((report, index) => (
                <div className={classes.reportCard} key={`report-${index}`}>
                  <div>
                    <strong>#{index + 1} Reason:</strong>{" "}
                    {report.report_type || "—"}
                  </div>
                  <div>
                    <strong>Message:</strong> {report.report_message || "—"}
                  </div>
                  <div>
                    <strong>Reporter:</strong>{" "}
                    {report.reporter_details?.reporter_name || "—"}
                    {report.reporter_details?.reporter_email
                      ? ` · ${report.reporter_details.reporter_email}`
                      : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onHide}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
}

function DetailRow({ label, value, multiline = false }) {
  return (
    <div className={classes.detailRow}>
      <span className={classes.detailLabel}>{label}</span>
      <span className={`${classes.detailValue} ${multiline ? classes.multiline : ""}`}>
        {value}
      </span>
    </div>
  );
}

export default PostDetailModal;
